/**
 * Portuguese (pt) translation pack for in-game content (weapons,
 * shields, engines, consumables, races, resources, ship classes).
 *
 * Why is this a separate file rather than `namePt`/`descriptionPt`
 * fields baked into every constant?
 *
 *   1. The constants in `packages/shared/src/constants/*.ts` are the
 *      gameplay source of truth — bumping the type to add yet another
 *      locale field would force every contributor to update hundreds of
 *      entries before the package compiles. A side-car table keeps the
 *      gameplay constants stable while still letting translators iterate
 *      on copy without touching balance code.
 *
 *   2. Anything *not* listed here falls through to English at render
 *      time (the documented behaviour of {@link t}). That gives us a
 *      free graceful-degradation path for partial translations: a new
 *      weapon ships in en/es first, and pt picks it up the moment a
 *      translator adds it to either this map or a `namePt` inline.
 *
 *   3. Operators can override anything here at runtime via the admin
 *      panel's `registerTranslation` hook — same as for en/es.
 *
 * Add a new entry by dropping a `<id>: '...'` line into the relevant
 * map below. Missing entries fall back to English.
 */
import { ResourceTier, ShipClass, RaceId } from '../types/entities.js';
/** Portuguese name for each weapon, keyed by weapon `id`. */
export const WEAPON_NAME_PT = {
    pulse_blaster: 'Blaster de Pulso',
    mining_laser: 'Laser de Mineração',
    gatling_cannon: 'Canhão Gatling',
    needle_repeater: 'Repetidor de Agulhas',
    light_missiles: 'Mísseis Leves',
    scatter_shotgun: 'Escopeta Dispersora',
    plasma_cannon: 'Canhão de Plasma',
    ion_beam: 'Raio Iônico',
    coil_driver: 'Impulsor de Bobina',
    arc_welder: 'Soldador de Arco',
    railgun: 'Canhão de Trilho',
    emp_torpedoes: 'Torpedos EMP',
    swarm_missiles: 'Mísseis de Enxame',
    prism_splitter: 'Divisor Prismático',
    disruptor_array: 'Matriz Disruptora',
    chrono_lance: 'Lança Crônica',
    cluster_rockets: 'Foguetes de Fragmentação',
    antimatter_howitzer: 'Obuseiro de Antimatéria',
    singularity_cannon: 'Canhão de Singularidade',
    nova_lance: 'Lança Nova',
    void_beam: 'Raio do Vazio',
    ether_harp: 'Harpa do Éter',
    abyssal_maw: 'Goela Abissal',
    sylphari_spore_rifle: 'Rifle de Esporos',
    tharix_phase_cutter: 'Cortador de Fase',
    chronos_temporal_shard: 'Estilhaço Temporal',
    krelg_rail_breaker: 'Quebra-Trilhos',
    nova_forge_lance: 'Lança da Forja',
    abyssal_chorus_voidsinger: 'Cantor do Vazio',
    pulse_repeater: 'Repetidor de Pulso',
    flak_burster: 'Estourador de Flak',
    harpoon_launcher: 'Lança-Arpões',
    graviton_cannon: 'Canhão de Grávitons',
    terran_mass_driver: 'Acelerador de Massa',
    zyrkath_acid_spitter: 'Cuspidor de Ácido',
    luminara_prism_lance: 'Lança Prismática',
    vokren_hidden_turret: 'Torre Oculta',
    obsidian_logic_disruptor: 'Disruptor Lógico',
    voidwalker_shadow_lance: 'Lança Sombria',
    paradox_lance: 'Lança Paradoxo',
    ghost_repeater: 'Repetidor Fantasma',
    hymn_cannon: 'Canhão de Hino',
    collapse_torch: 'Tocha de Colapso',
    starlace_array: 'Matriz Estrela-Renda',
    ether_loom: 'Tear do Éter',
    black_hole_caster: 'Lançador de Buraco Negro',
    aurora_choir: 'Coro Aurora',
    reality_breaker: 'Quebra-Realidade',
    infinity_lance: 'Lança Infinita',
    cosmic_decree: 'Decreto Cósmico',
};
/** Portuguese description for each weapon, keyed by weapon `id`. */
export const WEAPON_DESC_PT = {
    pulse_blaster: 'Blaster padrão confiável — tiros ilimitados e dano modesto.',
    mining_laser: 'Raio âmbar focado para quebrar minério; também serve como arma auxiliar mais forte que o blaster de pulso.',
    gatling_cannon: 'Canhão rotativo de alta cadência; ótimo para desgastar escudos.',
    needle_repeater: 'Lascas de silicato disparadas em velocidade vertiginosa.',
    light_missiles: 'Mísseis baratos sem guiamento — lentos, barulhentos e com impacto pesado.',
    scatter_shotgun: 'Cone de projéteis a curta distância. Cada chumbo rola seu próprio acerto.',
    plasma_cannon: 'Pesados projéteis de plasma violeta que derretem cascos.',
    ion_beam: 'Raio ciano contínuo — destrói escudos, raspa cascos.',
    coil_driver: 'Projéteis acelerados eletromagneticamente atravessam blindagens.',
    arc_welder: 'Arcos elétricos bifurcados que saltam entre placas próximas.',
    railgun: 'Projétil de trilho em alta velocidade; ciclo lento, impacto letal.',
    emp_torpedoes: 'Torpedos EMP que rasgam escudos antes de tocar a blindagem.',
    swarm_missiles: 'Pequenos mísseis guiados disparados em rajadas apertadas.',
    prism_splitter: 'Estilhaços cromáticos abrem em padrão de tripla dispersão.',
    disruptor_array: 'Matriz de pulsos que ignora parte dos escudos hostis.',
    chrono_lance: 'Lança em tempo lento; ignora queda do projétil e a maior parte da blindagem.',
    cluster_rockets: 'Foguetes que estouram em quatro submunições rastreadoras.',
    antimatter_howitzer: 'Projéteis de aniquilação que detonam em violenta floração matéria/antimatéria.',
    singularity_cannon: 'Colapsa uma microssingularidade no impacto; quase sempre fatal.',
    nova_lance: 'Lança de fogo estelar forjada pelos Forjadores Nova.',
    void_beam: 'Raio ancestral dos Caminhantes do Vazio que devora a luz ao viajar.',
    ether_harp: 'Cinco cordas harmônicas se lançam em uníssono ressonante.',
    abyssal_maw: 'Devora o que toca. Rara. Perigosa. Proibida.',
    sylphari_spore_rifle: 'Bioengenharia Sylphari — cápsulas estouram no impacto em uma nuvem cáustica.',
    tharix_phase_cutter: 'Tecnologia furtiva Tharix — atravessa escudos como se não existissem.',
    chronos_temporal_shard: 'Os Videntes Chronos dobram o tempo — cada tiro chega um instante antes de ser disparado.',
    krelg_rail_breaker: 'Railgun de couraçado Krelg reduzido para caber em casco de piloto.',
    nova_forge_lance: 'Forjada em cadinho de plasma Nova. Branco-incandescente, lenta, deleta o que beija.',
    abyssal_chorus_voidsinger: 'Cantada à existência pelo Coro Abissal. Escute bem — o tiro responde cantando.',
    pulse_repeater: 'Blaster de pulso automático — desgasta escudos com fogo constante.',
    flak_burster: 'Granadas de flak detonando no ar — quatro estilhaços abrem em leque a média distância.',
    harpoon_launcher: 'Arpões magnéticos pesados — lentos, mas perfuram blindagens.',
    graviton_cannon: 'Projétil de grávitons comprimido — atrai os disparos próximos para o ponto de impacto.',
    terran_mass_driver: 'Lançador cinético de indústria pesada Terran — utilitário e brutal.',
    zyrkath_acid_spitter: 'Bioarma Zyr\'kath — esguicha um arco de ácido vivo.',
    luminara_prism_lance: 'Raio facetado Luminara — refrata em escudos com dispersão surpreendente.',
    vokren_hidden_turret: 'Torre Vok\'ren — autodisparo silencioso ao alvo na mira.',
    obsidian_logic_disruptor: 'Projéteis do Coletivo Obsidiana — golpes cirúrgicos guiados por IA distribuída.',
    voidwalker_shadow_lance: 'Lança de fase dos Caminhantes — desliza entre dimensões antes de impactar.',
    paradox_lance: 'Lança paradoxo Eidolon — dispara dois feixes que chegam de direções opostas no mesmo instante.',
    ghost_repeater: 'Espada de honra Eidolon transformada em automática. Cada disparo atravessa escudos completamente.',
    hymn_cannon: 'Hino-explosão Leviatã Astral — uma nota do tamanho de uma cidade comprimida em um único projétil.',
    collapse_torch: 'Tocha Soberana carregada com uma estrela-colapso cativa. Um disparo, três mortes.',
    starlace_array: 'Matriz estrela-renda dispara rajada de cinco fios que tecem os impactos em um bordado de luz.',
    ether_loom: 'Três fios etéreos se entrelaçam em um único impacto — quanto mais tiros acertam, mais forte pulsa o próximo.',
    black_hole_caster: 'Arremessa uma singularidade domada. Seis tiros por viagem. A maioria dos pilotos só precisa de um.',
    aurora_choir: 'Coro aurora de seis vozes — cada projétil é uma nota e o acorde é fatal.',
    reality_breaker: 'Rasga um buraco na causalidade por onde o disparo passa. Qualquer coisa na linha é desfeita.',
    infinity_lance: 'Um único feixe disparado pela maior diagonal possível do universo visível.',
    cosmic_decree: 'Uma declaração assinada de remoção. O alvo deixa de existir antes do feixe chegar visivelmente.',
};
/** Portuguese name for each shield, keyed by shield `id`. */
export const SHIELD_NAME_PT = {
    basic_energy_shield: 'Escudo de Energia Básico',
    reinforced_plating: 'Blindagem Reforçada',
    ablative_coating: 'Revestimento Ablativo',
    solar_deflector: 'Defletor Solar',
    crystal_barrier: 'Barreira de Cristal',
    bioweave_mesh: 'Malha Bio-tecida',
    phase_shield: 'Escudo de Fase',
    chrono_aegis: 'Égide Crônica',
    prism_field: 'Campo Prismático',
    void_barrier: 'Barreira do Vazio',
    singularity_skin: 'Pele de Singularidade',
    nova_corona: 'Coroa Nova',
    echo_screen: 'Tela de Eco',
    abyssal_ward: 'Guardião Abissal',
    quantum_bulwark: 'Baluarte Quântico',
    ether_veil: 'Véu do Éter',
    sylphari_bioweave: 'Escudo Biotecido',
    tharix_cloak_shield: 'Campo de Camuflagem',
    abyssal_null_barrier: 'Barreira Nula',
    kinetic_buffer: 'Amortecedor Cinético',
    static_field: 'Campo Estático',
    aegis_array: 'Matriz Égide',
    composite_armor: 'Blindagem Compósita',
    ablative_lattice: 'Treliça Ablativa',
    terran_modular_plating: 'Blindagem Modular',
    vokren_ironclad_shield: 'Escudo Couraçado',
    sylphari_blossom_field: 'Campo de Florescer',
    obsidian_nano_aegis: 'Nano Égide',
    krelg_warhammer_aegis: 'Égide Martelo de Guerra',
    mirror_aegis: 'Égide Espelho',
    leviathan_skin: 'Pele de Leviatã',
    collapse_lattice: 'Treliça de Colapso',
    starforge_rampart: 'Muralha Forja Estelar',
    aurora_veil: 'Véu Aurora',
    infinity_aegis: 'Égide Infinita',
};
/** Portuguese description for each shield, keyed by shield `id`. */
export const SHIELD_DESC_PT = {
    basic_energy_shield: 'Bolha energética padrão — nada de especial, nunca quebra.',
    reinforced_plating: 'Blindagem fixa que absorve dano cinético; fraca contra energia.',
    ablative_coating: 'Camada cerâmica de sacrifício que se solta sob impactos explosivos.',
    solar_deflector: 'Campo solar que resiste surpreendentemente bem a armas de feixe.',
    crystal_barrier: 'Treliça cristalina facetada que canta ao ser atingida por energia.',
    bioweave_mesh: 'Micélio Sylphari autorregenerativo que se reentrelaça entre combates.',
    phase_shield: 'Faseia parcialmente a nave — muitos erros, pouca absorção.',
    chrono_aegis: 'Égide em loop temporal que rebobina pequenas frações do dano recebido.',
    prism_field: 'Casca refratora que desvia feixes inofensivamente ao redor do casco.',
    void_barrier: 'Baluarte forjado no vazio; excelente proteção geral.',
    singularity_skin: 'Pele de espaço curvado que redireciona impactos para microburacos negros.',
    nova_corona: 'Coroa estelar que vaporiza o fogo energético recebido.',
    echo_screen: 'Tela de eco que reflete uma parte do dano de volta ao atacante.',
    abyssal_ward: 'Guardião do vazio profundo — absorve energia quase indefinidamente.',
    quantum_bulwark: 'Baluarte quântico — só existe quando observado, esquiva e absorve por turnos.',
    ether_veil: 'Véu etéreo que torna a nave parcialmente intangível.',
};
/** Portuguese name for each engine, keyed by engine `id`. */
export const ENGINE_NAME_PT = {
    basic_thruster: 'Propulsor Básico',
    ion_drive: 'Propulsor Iônico',
    plasma_drive: 'Propulsor de Plasma',
    warp_engine: 'Motor de Dobra',
    sylphari_bloom_drive: 'Motor de Florescer',
    chronos_temporal_drive: 'Motor Temporal',
    pulse_thruster: 'Propulsor Pulsado',
    ion_drive_mk2: 'Propulsor Iônico Mk II',
    solar_sail: 'Vela Solar',
    quantum_drift: 'Deriva Quântica',
    zyrkath_pulse_drive: 'Bio-Propulsor Pulsado',
    voidwalker_phase_drive: 'Motor de Fase',
    tharix_silent_drive: 'Motor Silente',
    krelg_overpressured_drive: 'Motor Sobrepressurizado',
    nova_starforge_drive: 'Motor Forja Estelar',
};
/** Portuguese description for each engine, keyed by engine `id`. */
export const ENGINE_DESC_PT = {
    basic_thruster: 'Propulsor padrão — confiável, modesto.',
    ion_drive: 'Aceleração suave e estável; queima de iões eficiente.',
    plasma_drive: 'Empuxo violento de plasma; consome bastante energia.',
    warp_engine: 'Comprime o espaço atrás da nave para velocidades superluminais.',
    sylphari_bloom_drive: 'Motor vivo Sylphari — pulsa em ondas orgânicas.',
    chronos_temporal_drive: 'Manipula o fluxo do tempo local para impulso aparente.',
    pulse_thruster: 'Propulsor pulsado — empuxos curtos e ágeis.',
    ion_drive_mk2: 'Versão melhorada do propulsor iônico, com mais empuxo.',
    solar_sail: 'Vela solar — aproveita o vento estelar para empurrar.',
    quantum_drift: 'Deriva quântica — atalhos pelo espaço-tempo.',
    zyrkath_pulse_drive: 'Bio-pulsos Zyr\'kath para empuxo orgânico violento.',
    voidwalker_phase_drive: 'Motor de fase — desliza entre dimensões.',
    tharix_silent_drive: 'Motor silente — desaparece dos sensores.',
    krelg_overpressured_drive: 'Motor Krelg sobrepressurizado — bruto e poderoso.',
    nova_starforge_drive: 'Motor forjado em estrela — empuxo estelar puro.',
};
/** Portuguese name for each consumable, keyed by consumable `id`. */
export const CONSUMABLE_NAME_PT = {
    repair_kit_small: 'Kit de Reparo Pequeno',
    repair_kit_medium: 'Kit de Reparo Médio',
    repair_kit_large: 'Kit de Reparo Grande',
    repair_kit_epic: 'Enxame de Nanitos',
    repair_kit_xl: 'Kit de Reparo XL',
    repair_kit_legendary: 'Kit Lendário de Casco',
    shield_battery_small: 'Bateria de Escudo Pequena',
    shield_battery_medium: 'Bateria de Escudo Média',
    shield_battery_large: 'Bateria de Escudo Grande',
    shield_battery_xl: 'Bateria de Escudo XL',
    shield_battery_legendary: 'Célula Lendária de Escudo',
    thrust_booster_short: 'Propulsor Turbo',
    thrust_booster_long: 'Propulsor Turbo Estendido',
    thrust_booster_apex: 'Frasco de Empuxo Ápice',
    thrust_overcharge_eternal: 'Sobrecarga Eterna de Empuxo',
    damage_booster_short: 'Estimulante de Combate',
    damage_booster_long: 'Munição Sobrecarregada',
    damage_booster_apex: 'Ira do Soberano',
    damage_overdrive: 'Sobrecarga de Dano',
    emergency_warp_kit: 'Kit de Salto de Emergência',
    sylphari_regen_pod: 'Cápsula Regen Sylphari',
    chronos_chrono_lens: 'Lente Crônica',
    nova_combat_stim: 'Estimulante Nova',
    terran_field_kit: 'Kit de Campo Terran',
    luminara_focus_lens: 'Lente de Foco Luminara',
    vokren_ammo_drum: 'Tambor de Munição Vokren',
    krelg_battle_stim: 'Estimulante de Combate Krelg',
    sylphari_bloom_pod: 'Cápsula Florescer Sylphari',
    chronos_paradox_kit: 'Kit Paradoxo Crônico',
    memory_imprint_chip: 'Chip de Memória',
    cargo_lockbox: 'Cofre de Carga',
    leviathan_lullaby: 'Cantiga do Leviatã',
    ether_lattice: 'Treliça do Éter',
    infinity_repair_swarm: 'Enxame Reparador Infinito',
    premium_quantum_repair: 'Célula Quântica de Reparo',
    premium_overdrive_serum: 'Soro de Sobrecarga',
    premium_aegis_implant: 'Implante Égide',
};
/** Portuguese description for each consumable, keyed by consumable `id`. */
export const CONSUMABLE_DESC_PT = {
    repair_kit_small: 'Kit de campo básico que repara um pedaço do casco em segundos.',
    repair_kit_medium: 'Kit de reparo padrão para combates curtos.',
    repair_kit_large: 'Kit pesado de reparo — restaura grande parte do casco.',
    repair_kit_epic: 'Enxame de nanitos que reconstroem o casco molécula por molécula.',
    shield_battery_small: 'Pequena bateria que recarrega rapidamente o escudo.',
    shield_battery_medium: 'Bateria média de escudo para combates prolongados.',
    shield_battery_large: 'Bateria grande de escudo — recarrega quase totalmente.',
    thrust_booster_short: 'Aumenta o empuxo da nave por alguns segundos.',
    thrust_booster_long: 'Aumenta o empuxo da nave por um período prolongado.',
    damage_booster_short: 'Estimulante químico que amplifica o dano por alguns segundos.',
    damage_booster_long: 'Munição modificada que entrega golpes muito mais fortes por mais tempo.',
    memory_imprint_chip: 'Salva sua memória de XP — preserva XP em caso de morte (uso único automático).',
    cargo_lockbox: 'Cofre selado — preserva a carga em caso de morte (uso único automático).',
    emergency_warp_kit: 'Salto de emergência para a estação mais próxima.',
};
/** Portuguese name for each race, keyed by `RaceId`. */
export const RACE_NAME_PT = {
    [RaceId.Terran]: 'Terran',
    [RaceId.Zyrkath]: "Zyr'kath",
    [RaceId.Luminara]: 'Luminara',
    [RaceId.Vokren]: "Vok'ren",
    [RaceId.ObsidianCollective]: 'Coletivo Obsidiana',
    [RaceId.VoidWalker]: 'Caminhante do Vazio',
    [RaceId.Sylphari]: 'Sylphari',
    [RaceId.KrelgHegemony]: 'Hegemonia Krelg',
    [RaceId.TharixCabal]: 'Cabala Tharix',
    [RaceId.ChronosSeers]: 'Videntes de Chronos',
    [RaceId.NovaForgers]: 'Forjadores Nova',
    [RaceId.AbyssalChorus]: 'Coro Abissal',
    [RaceId.EidolonConclave]: 'Conclave Eidolon',
    [RaceId.AstralLeviathan]: 'Leviatã Astral',
    [RaceId.SingularitySovereign]: 'Soberanos da Singularidade',
};
/** Portuguese description for each race, keyed by `RaceId`. */
export const RACE_DESC_PT = {
    [RaceId.Terran]: 'Exploradores equilibrados da Terra',
    [RaceId.Zyrkath]: 'Enxame insectoide agressivo',
    [RaceId.Luminara]: 'Seres de energia cristalina pacíficos',
    [RaceId.Vokren]: 'Comerciantes e mercadores nômades',
    [RaceId.ObsidianCollective]: 'Inteligência de máquina lógica',
    [RaceId.VoidWalker]: 'Raça antiga e misteriosa',
    [RaceId.Sylphari]: 'Bio-arquitetos vegetais com porta-naves vivos',
    [RaceId.KrelgHegemony]: 'Clãs guerreiros pilotando couraçados industriais',
    [RaceId.TharixCabal]: 'Infiltradores silíceos, mestres em cruzadores furtivos',
    [RaceId.ChronosSeers]: 'Tecelões do tempo em escunas cristalinas',
    [RaceId.NovaForgers]: 'Titãs de couraçados forjados em plasma',
    [RaceId.AbyssalChorus]: 'Leviatãs do vazio profundo que harmonizam a realidade',
    [RaceId.EidolonConclave]: 'Cavaleiros paradoxos com cascos espelho, além da mortalidade',
    [RaceId.AstralLeviathan]: 'Bestas intergalácticas cantantes pilotando naves capitais vivas',
    [RaceId.SingularitySovereign]: 'Imperadores do horizonte de eventos que pilotam micro-buracos negros armados',
};
/** Portuguese name for each resource tier. */
export const RESOURCE_NAME_PT = {
    [ResourceTier.ScrapMetal]: 'Sucata',
    [ResourceTier.CrystalShards]: 'Estilhaços de Cristal',
    [ResourceTier.PlasmaCore]: 'Núcleo de Plasma',
    [ResourceTier.VoidEssence]: 'Essência do Vazio',
    [ResourceTier.DarkMatter]: 'Matéria Escura',
    [ResourceTier.QuantumFragment]: 'Fragmento Quântico',
    [ResourceTier.IronOre]: 'Minério de Ferro',
    [ResourceTier.CopperOre]: 'Minério de Cobre',
    [ResourceTier.Silicate]: 'Silicato',
    [ResourceTier.TitaniumAlloy]: 'Liga de Titânio',
    [ResourceTier.SolarPlasma]: 'Plasma Solar',
    [ResourceTier.Bioflux]: 'Biofluxo',
    [ResourceTier.NeutroniumDust]: 'Pó de Neutrônio',
    [ResourceTier.ChronoQuartz]: 'Quartzo Crônico',
    [ResourceTier.VoidPearl]: 'Pérola do Vazio',
    [ResourceTier.PhasePrism]: 'Prisma de Fase',
    [ResourceTier.StarforgeIngot]: 'Lingote Forja Estelar',
    [ResourceTier.EchoCrystal]: 'Cristal de Eco',
    [ResourceTier.SingularityShard]: 'Fragmento de Singularidade',
    [ResourceTier.AbyssalTear]: 'Lágrima Abissal',
    [ResourceTier.EtherealEssence]: 'Essência Etérea',
};
/** Portuguese name for each ship class. */
export const SHIP_NAME_PT = {
    [ShipClass.Scout]: 'Explorador',
    [ShipClass.Fighter]: 'Caça',
    [ShipClass.Interceptor]: 'Interceptor',
    [ShipClass.Cruiser]: 'Cruzador',
    [ShipClass.Freighter]: 'Cargueiro',
    [ShipClass.Destroyer]: 'Destruidor',
    [ShipClass.Mothership]: 'Nave-Mãe',
};
//# sourceMappingURL=content-pt.js.map